import type { Collapsible } from '@/shared/types/sidebar';
import { cva } from 'class-variance-authority';

/**
 * Static config for the app sidebar. The desktop open/collapsed flag is stored
 * in a cookie (read/written by the sidebar Zustand store,
 * `src/shared/stores/sidebarStore.ts`); the ⌘/Ctrl-B toggle shortcut is
 * registered app-wide in `src/app/shortcutRegistry.ts`.
 */

/**
 * How the single app sidebar collapses — an icon rail. The app is docked left
 * with the standard surface (hardcoded as `data-side`/`data-variant` on the
 * `AppSidebar` shell), and only the collapse mode is referenced at runtime (via
 * `useSidebar().collapsible`). The `--sidebar-width` dimensions live in `:root`
 * in `src/styles/index.css`.
 */
export const SIDEBAR_COLLAPSIBLE: Collapsible = 'icon';

/** Cookie name holding the desktop sidebar open (`true`/`false`) state. */
export const SIDEBAR_COOKIE_NAME = 'sidebar_state';

/** How long the sidebar-state cookie persists (7 days, in seconds). */
export const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;

/** Stable keys for the sidebar-list skeleton's placeholder rows (row count = length). */
export const SIDEBAR_SKELETON_ROWS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const;

export const appSidebarVariants = cva(
  'group sticky top-0 flex h-svh shrink-0 flex-col border-sidebar-border border-r bg-sidebar text-sidebar-foreground transition-[width] duration-200 ease-linear',
  {
    variants: {
      open: {
        true: 'w-[var(--sidebar-width)]',
        false: 'w-[var(--sidebar-width-icon)]',
      },
    },
    defaultVariants: { open: true },
  },
);

export const sidebarToggleIconVariants = cva('transition-transform duration-300', {
  variants: {
    open: {
      true: 'rotate-180',
      false: null,
    },
  },
  defaultVariants: { open: true },
});

export const permissionNavSkeletonVariants = cva(null, {
  variants: {
    mobile: {
      true: 'space-y-2 px-1 pt-4',
      false: 'space-y-1 px-1.5 pt-2',
    },
  },
  defaultVariants: { mobile: false },
});

export const navScrollVariants = cva('min-h-0 flex-1 overflow-y-auto', {
  variants: { mobile: { true: 'px-4 py-5', false: 'px-1.5 py-1' } },
  defaultVariants: { mobile: false },
});

export const navGroupStackVariants = cva('flex flex-col', {
  variants: { mobile: { true: 'gap-6', false: 'gap-2' } },
  defaultVariants: { mobile: false },
});

export const navLabelWrapVariants = cva(null, {
  variants: { mobile: { true: 'px-1 pb-2', false: 'px-1.5 pb-0.5' } },
  defaultVariants: { mobile: false },
});

export const navItemListVariants = cva('flex w-full min-w-0 flex-col', {
  variants: { mobile: { true: 'gap-1', false: 'gap-px' } },
  defaultVariants: { mobile: false },
});

export const navItemButtonVariants = cva('text-muted-foreground', {
  variants: {
    mobile: {
      true: 'h-12 rounded-lg px-3.5 text-[15px] font-medium data-[active=true]:text-foreground ring-0 focus:ring-0 focus-visible:ring-0 [&>svg]:size-5',
      false: null,
    },
  },
  defaultVariants: { mobile: false },
});

export const navLabelSkeletonVariants = cva('rounded', {
  variants: { mobile: { true: 'h-3.5 w-20', false: 'h-3 w-16' } },
  defaultVariants: { mobile: false },
});

export const navItemSkeletonVariants = cva('w-full rounded-lg', {
  variants: { mobile: { true: 'h-12', false: 'h-7 rounded-md' } },
  defaultVariants: { mobile: false },
});

export const accountFooterVariants = cva('mt-auto shrink-0', {
  variants: { mobile: { true: 'border-border border-t px-4 py-4', false: 'p-1.5' } },
  defaultVariants: { mobile: false },
});

export const accountTriggerVariants = cva(
  'h-auto gap-1.5 !p-1.5 group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-1',
  {
    variants: { mobile: { true: 'h-14 gap-3 rounded-lg !p-2.5', false: null } },
    defaultVariants: { mobile: false },
  },
);

export const accountAvatarVariants = cva(
  'flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted',
  {
    variants: { mobile: { true: 'size-10', false: 'size-6' } },
    defaultVariants: { mobile: false },
  },
);

export const accountInitialsVariants = cva('font-medium text-muted-foreground', {
  variants: { mobile: { true: 'text-sm', false: 'text-[10px]' } },
  defaultVariants: { mobile: false },
});

export const accountNameVariants = cva('line-clamp-1 truncate font-medium leading-tight', {
  variants: { mobile: { true: 'text-sm', false: 'text-xs' } },
  defaultVariants: { mobile: false },
});

export const accountEmailVariants = cva(
  'line-clamp-1 truncate text-muted-foreground leading-tight',
  {
    variants: { mobile: { true: 'text-xs', false: 'text-[10px]' } },
    defaultVariants: { mobile: false },
  },
);

export const navSearchIconVariants = cva(
  '-translate-y-1/2 pointer-events-none absolute top-1/2 text-muted-foreground',
  {
    variants: { size: { comfortable: 'left-3 size-4', compact: 'left-2 size-3' } },
    defaultVariants: { size: 'compact' },
  },
);

export const navSearchInputVariants = cva(
  'border-0 bg-muted/40 shadow-none ring-0 placeholder:text-muted-foreground/70 focus-visible:bg-muted/60 focus-visible:ring-1 focus-visible:ring-ring/30',
  {
    variants: {
      size: {
        comfortable: 'h-11 rounded-xl py-0 pl-10 text-[15px]',
        compact: 'h-7 py-0 pl-7 text-xs',
      },
      hasValue: { true: null, false: null },
    },
    compoundVariants: [
      { size: 'comfortable', hasValue: true, class: 'pr-10' },
      { size: 'compact', hasValue: true, class: 'pr-7' },
    ],
    defaultVariants: { size: 'compact', hasValue: false },
  },
);

export const navSearchClearButtonVariants = cva(
  '-translate-y-1/2 absolute top-1/2 flex items-center justify-center rounded-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring',
  {
    variants: { size: { comfortable: 'right-2 size-7', compact: 'right-1 size-5' } },
    defaultVariants: { size: 'compact' },
  },
);

export const navSearchClearIconVariants = cva(null, {
  variants: { size: { comfortable: 'size-4', compact: 'size-3' } },
  defaultVariants: { size: 'compact' },
});

export const navSectionLabelVariants = cva('font-medium text-muted-foreground', {
  variants: {
    size: { comfortable: 'text-xs tracking-wider', compact: 'text-[11px] tracking-wide' },
  },
  defaultVariants: { size: 'compact' },
});

export const navSectionCountVariants = cva('text-muted-foreground tabular-nums', {
  variants: { size: { comfortable: 'text-xs', compact: 'text-[11px]' } },
  defaultVariants: { size: 'compact' },
});

export const sidebarMenuButtonVariants = cva(
  'peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        primary:
          'bg-primary text-primary-foreground hover:bg-primary-hovered hover:text-primary-foreground',
        outline:
          'border border-border bg-background shadow-[0_0_0_1px_rgb(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_rgb(var(--sidebar-accent))]',
      },
      size: {
        default: 'h-9 text-sm font-medium',
        sm: 'h-7 text-xs',
        lg: 'h-12 p-3.5 text-sm group-data-[collapsible=icon]:!p-3.5 group-data-[collapsible=icon]:!size-12 [&>svg]:size-5',
      },
      mobile: { true: 'ring-0 focus:ring-0 focus-visible:ring-0', false: null },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      mobile: false,
    },
  },
);
